// src/hooks/useRecompra.js
import { useMemo } from "react";

function toMs(val) {
  if (!val) return null;
  const d = val?.toDate ? val.toDate() : new Date(val);
  return isNaN(d.getTime()) ? null : d.getTime();
}

// Calcula el nivel de urgencia de recompra para cada cliente
export function useRecompra(clientes, pedidos, reuniones = []) {
  return useMemo(() => {
    if (!clientes?.length || !pedidos?.length) return [];

    const ahora = Date.now();

    // Índice de reuniones recientes por clienteId (últimos 14 días)
    const hace14d = ahora - 14 * 86400000;
    const contactadosReciente = new Set(
      reuniones
        .filter(r => {
          const t = toMs(r.creadoEn) || toMs(r.fecha);
          return t && t >= hace14d;
        })
        .map(r => r.clienteId)
        .filter(Boolean)
    );

    // Pedidos entregados agrupados por clienteId
    const pedidosPorCliente = {};
    pedidos.forEach(p => {
      if (p.estado !== "Entregado" || !p.clienteId) return;
      if (!pedidosPorCliente[p.clienteId]) pedidosPorCliente[p.clienteId] = [];
      pedidosPorCliente[p.clienteId].push(p);
    });

    const resultado = [];

    Object.entries(pedidosPorCliente).forEach(([clienteId, peds]) => {
      const cliente = clientes.find(c => c.id === clienteId);
      if (!cliente) return;

      // Ordenar por fecha
      const ordenados = peds
        .map(p => ({ ...p, _ms: toMs(p.creadoEn) || 0 }))
        .filter(p => p._ms > 0)
        .sort((a, b) => a._ms - b._ms);

      if (ordenados.length === 0) return;

      const ultimo = ordenados[ordenados.length - 1];
      const diasDesdeUltima = Math.round((ahora - ultimo._ms) / 86400000);

      // Frecuencia promedio entre compras
      let frecuenciaPromedio = null;
      if (ordenados.length >= 2) {
        const gaps = [];
        for (let i = 1; i < ordenados.length; i++) {
          gaps.push(Math.round((ordenados[i]._ms - ordenados[i-1]._ms) / 86400000));
        }
        frecuenciaPromedio = Math.round(gaps.reduce((s, g) => s + g, 0) / gaps.length);
      }

      // Nivel de riesgo
      let nivelRiesgo;
      if (frecuenciaPromedio) {
        const ratio = diasDesdeUltima / frecuenciaPromedio;
        if (ratio >= 2)    nivelRiesgo = "inactivo";
        else if (ratio >= 1.5) nivelRiesgo = "frío";
        else if (ratio >= 0.9) nivelRiesgo = "tibio";
        else                   nivelRiesgo = "caliente";
      } else {
        // Sin frecuencia calculable — usar días absolutos
        if (diasDesdeUltima >= 120)     nivelRiesgo = "inactivo";
        else if (diasDesdeUltima >= 60) nivelRiesgo = "frío";
        else if (diasDesdeUltima >= 30) nivelRiesgo = "tibio";
        else                            nivelRiesgo = "caliente";
      }

      // Valor del último pedido
      const valorUltima = Number(ultimo.valor) || 0;

      // Material / referencia del último pedido
      const ultimoRef = ultimo.codigoSeguimiento || ultimo.materiales || "";

      resultado.push({
        clienteId,
        clienteNombre:    cliente.nombre || "",
        clienteEmpresa:   cliente.empresa || "",
        responsable:      cliente.responsable || "",
        telefono:         cliente.telefono || "",
        email:            cliente.email || "",
        diasDesdeUltima,
        frecuenciaPromedio,
        nivelRiesgo,
        valorUltima,
        ultimoRef,
        totalCompras:     peds.length,
        contactadoReciente: contactadosReciente.has(clienteId),
      });
    });

    // Ordenar: inactivo > frío > tibio > caliente, luego por días
    const ORDEN = { inactivo: 0, frío: 1, tibio: 2, caliente: 3 };
    return resultado.sort((a, b) => {
      const o = ORDEN[a.nivelRiesgo] - ORDEN[b.nivelRiesgo];
      return o !== 0 ? o : b.diasDesdeUltima - a.diasDesdeUltima;
    });
  }, [clientes, pedidos, reuniones]);
}
